import {
	CheckboxField,
	CurrencyInputField,
	CurrencyTypeProvider,
	InfoIconTooltipPopover,
	TextareaField,
} from "@dgs/core";
import { isAfter } from "date-fns";
import { useField } from "formik";
import { FC, Fragment } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Currency } from "../../types/currency";
import { CostCategoryRequirement, EventTypeOptionResource } from "../../types/eventType";
import { CurrencySingleSelect } from "../../ui/CurrencySingleSelect";
import { EventFormColumns } from "./EventFormColumns";
import { shouldSkipPlannedCostStep } from "./eventStepperUtils";
import { useEventTypeField } from "./useEventTypeField";

export const InfoText = styled.p`
	${({ theme }) => theme.content.text};
	padding-bottom: ${({ theme }) => theme.spacing(4)};
`;

export const Header = styled.div`
	display: grid;
	grid-template-columns: max-content max-content;
	align-items: center;
	grid-gap: ${({ theme }) => theme.spacing(2)};
	padding: ${({ theme }) => theme.spacing(4)} 0;
`;

export const Heading = styled.div`
	font-size: ${({ theme }) => theme.fonts.sizes.normal};
	font-weight: ${({ theme }) => theme.fonts.weights.bold};
`;

export const isRequired = (eventType: EventTypeOptionResource | null, item: CostCategoryRequirement): boolean =>
	eventType?.costCategoryRequirements.find((c) => c.costCategory.id === item.costCategory.id)?.requirement ===
	"required";

export const EventPlannedCostStepFormFields: FC = () => {
	const { t } = useTranslation();
	const [{ value }] = useField<Currency>("currency");
	const [{ value: end }] = useField<string>("end");
	const eventType = useEventTypeField();

	if (shouldSkipPlannedCostStep(eventType)) {
		return null;
	}

	return (
		<>
			<InfoText>{t("Please enter the calculated total budget from the budget planning here.")}</InfoText>
			<CurrencySingleSelect
				name="currency"
				label={t("Currency")}
				required={eventType?.costCategoryRequirements.some((c) => c.requirement === "required")}
			/>
			<CurrencyTypeProvider currencyType={value ? value : undefined}>
				{eventType?.costCategoryRequirements
					.filter((costCategoryRequirement) => costCategoryRequirement.requirement !== "prohibited")
					.map((item, index) => (
						<Fragment key={`${item.costCategory.id}`}>
							<Header>
								<Heading>{item.costCategory.name}</Heading>
								{item.costCategory.description && (
									<InfoIconTooltipPopover>{item.costCategory.description}</InfoIconTooltipPopover>
								)}
							</Header>
							<EventFormColumns>
								<CurrencyInputField
									name={`eventCostCategories.${index}.plannedCost`}
									label={t("Planned cost")}
									required={isRequired(eventType, item)}
								/>
								<CurrencyInputField
									name={`eventCostCategories.${index}.actualCost`}
									label={t("Actual cost")}
									required={isRequired(eventType, item) && isAfter(new Date(), new Date(end))}
								/>
							</EventFormColumns>
							<CheckboxField
								label={t("I would like to write a comment")}
								name={`eventCostCategories.${index}.commentAllowed`}
							/>
							<CommentField index={index} />
						</Fragment>
					))}
			</CurrencyTypeProvider>
		</>
	);
};

const CommentField: FC<{ index: number }> = ({ index }) => {
	const { t } = useTranslation();
	const [{ value }] = useField<boolean>(`eventCostCategories.${index}.commentAllowed`);

	if (!value) {
		return null;
	}

	return <TextareaField name={`eventCostCategories.${index}.comment`} label={t("Comment")} />;
};
